<script setup lang="ts">
import { i18n } from '@/i18n';
import Modal from '@/components/modal/Modal.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { watch } from 'vue';
import { useRegisterOrderModalStore } from './useRegisterOrderModalStore';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';

// props
const props = withDefaults(
  defineProps<{
    modalState?: boolean;
  }>(),
  {
    modalMenuItem: undefined,
    modalState: false,
  }
);

// emits
const emits = defineEmits<{
  (e: 'closeModal'): void;
  (e: 'exportOrder', file?: File): void;
}>();

//変数
const t = i18n.t;

const store = useRegisterOrderModalStore();
const { state } = storeToRefs(store);

watch(
  () => props.modalState,
  () => {
    if (!props.modalState) {
      store.$reset();
    }
  }
);

/**
 * 出力ボタンのdisabled判定
 */
const isDisabledExportButton = computed(() => {
  return state.value.selectedFile === undefined;
});

/**
 * ファイル選択ボタン押下
 */
const onClickSelectFileButton = () => {
  // ファイル選択を発火させる
  document.getElementById('file-input')?.click();
};

/**
 * ファイル選択時の処理
 * @param event - イベント
 */
const handleFileChange = (event: Event) => {
  if (!(event.target instanceof HTMLInputElement)) return;
  const file = event.target.files?.[0];
  if (!file) return;
  state.value.selectedFile = file;
};
</script>

<template>
  <Modal
    class="export-order-modal"
    :modal-size="400"
    :stretch-container="true"
    :scroll-off="true"
    :padding="'24px 32px'"
    :modal-state="modalState"
    :border-radius="'16px'"
    @outside-click="emits('closeModal')"
  >
    <div class="export-order-modal__content-container">
      <div class="export-order-modal__header">
        {{ t('発注の取込') }}
      </div>

      <div class="export-order-modal__main-content main-content">
        <div class="main-content__select-format select-format">
          <div class="select-format__label">
            {{ t('エクセルを選択してください') }}
          </div>
          <TextButton
            :button-style="'btn-primary'"
            :size="'btn-md'"
            :on-click="onClickSelectFileButton"
            :disabled="false"
            class="select-format__select-file-button"
          >
            {{ t('取込ファイル＋') }}
          </TextButton>
          <input id="file-input" class="select-format__file-input" type="file" @change="handleFileChange($event)" />
          <!-- TODO: 24-07-20 書式を選択するUI、ひとまず一律でデフォルト書式にするのでコメントアウト -->
          <!-- <div class="select-format__content-container">
            <div class="select-format__radio-container">
              <input
                id="default-format"
                v-model="state.selectedFormat"
                class="select-format__radio"
                type="radio"
                name="format"
                value="default"
              />
              <label class="select-format__radio-label" for="default-format">{{ t('デフォルト書式') }}</label>
            </div>
            <div class="select-format__radio-container">
              <input
                id="custom-format"
                v-model="state.selectedFormat"
                class="select-format__radio"
                type="radio"
                name="format"
                value="custom"
              />
              <label class="select-format__radio-label" for="custom-format">{{ t('ユーザー設定書式') }}</label>
            </div>
          </div> -->
        </div>
      </div>

      <div class="export-order-modal__footer footer">
        <TextButton class="footer__button footer__button--cancel" @click="() => emits('closeModal')">
          {{ t('もどる') }}
        </TextButton>
        <TextButton
          class="footer__button footer__button--confirm"
          :disabled="isDisabledExportButton"
          @click="emits('exportOrder', state.selectedFile)"
        >
          {{ t('取込') }}
        </TextButton>
      </div>
    </div>
  </Modal>
</template>

<style lang="scss" scoped>
.export-order-modal {
  &__content-container {
    height: 100%;
    display: grid;
    grid-template-rows: repeat(3, auto);
    gap: 24px;
  }

  &__header {
    color: $textDefault;
    font-size: 22px;
    font-weight: 700;
    line-height: 28.6px;
    width: 100%;
    text-align: center;
  }

  &__footer {
    display: grid;
    grid-template-columns: repeat(2, fit-content(50%));
  }
}

.main-content {
  height: 100%;
  width: 100%;
  display: grid;
  grid-template-rows: repeat(2, auto);
  align-content: space-between;
}

.select-format {
  display: grid;
  grid-template-rows: repeat(2, auto);
  gap: 16px;

  &__label {
    font-size: 16px;
    line-height: 16px;
    font-weight: 700;
    color: $textDefault;
  }

  &__content-container {
    display: grid;
    grid-template-rows: repeat(2, auto);
    gap: 8px;
    padding-left: 16px;
  }

  // TODO: 24-07-20 書式を選択するUI、ひとまず一律でデフォルト書式にするのでコメントアウト
  // &__radio-container {
  //   display: grid;
  //   grid-template-columns: repeat(2, auto);
  //   gap: 4px;
  //   justify-content: start;
  // }

  // &__radio-label {
  //   font-size: 14px;
  //   line-height: 20.27px;
  //   font-weight: 400;
  // }

  &__select-file-button {
    width: 160px;
    height: 40px;
    margin-left: 16px;
  }

  &__file-input {
    display: none;
  }
}

.footer {
  display: grid;
  grid-template-columns: repeat(2, auto);
  justify-self: center;
  gap: 10px;

  &__button {
    width: 160px;
    height: 40px;
    font-size: 16px;
    line-height: 16px;
    font-weight: 700;

    &--cancel {
      background-color: $semanticWhite;
      border: 1px solid $semanticPrimary;
      color: $semanticPrimary;
    }

    &--confirm {
      background-color: $semanticPrimary;
      color: $semanticWhite;
    }
  }
}
</style>
